<template>
  <PayOpsModalShell
    :visible="visible"
    :title="`Review ${actionLabel}`"
    :subtitle="`${selectedOrderUuids.length} of ${orders.length} order${orders.length === 1 ? '' : 's'} selected`"
    :icon="actionIcon"
    :tone="actionTone"
    :close-disabled="isSubmitting"
    @close="$emit('close')"
  >
    <div v-if="completedOrders.length > 0" class="alert alert-warning border d-flex align-items-start gap-2 mb-3">
      <i class="fa fa-exclamation-triangle text-warning mt-1"></i>
      <div>
        <strong class="d-block mb-1">Attention: Already Completed Orders Detected</strong>
        <span>
          {{ completedOrders.length }} order(s) in this selection are already in a final state (<code>COMPLETED</code> or <code>COMPLETED_MANUALLY</code>).
          They have been left unselected. Tick them only if you want to re-amend / force-complete them.
        </span>
      </div>
    </div>

    <div v-if="blockedByRole" class="alert alert-danger border d-flex align-items-start gap-2 mb-3">
      <i class="fa fa-ban text-danger mt-1"></i>
      <div>
        <strong class="d-block mb-1">Admin role required</strong>
        <span>
          This selection escalates to <code>{{ effectiveAction }}</code>, which only
          <code>pay_operations_admin</code> may run. Unselect the already-completed orders, or
          ask an admin to execute this batch.
        </span>
      </div>
    </div>

    <div class="alert alert-light border d-flex align-items-start gap-2 mb-3">
      <i class="fa fa-info-circle text-primary mt-1"></i>
      <span>Review the selected orders and provide an audit reason before creating the bulk job.</span>
    </div>

    <div class="row g-3 mb-4">
      <div class="col-12">
        <label class="form-label fw-semibold">Reason <span class="text-danger">*</span></label>
        <textarea
          v-model="reason"
          class="form-control border border-primary"
          rows="3"
          placeholder="Explain why this bulk amendment is needed..."
          :disabled="isSubmitting"
        ></textarea>
        <div class="form-text" :class="{ 'text-danger': reason.length > 0 && reason.length < 10 }">
          {{ reason.length }} / 10 minimum characters
        </div>
      </div>
      <div v-if="supportsCapture" class="col-12">
        <div class="form-check form-switch">
          <input
            id="bulkReviewCapture"
            v-model="captureOnAmend"
            class="form-check-input border border-primary"
            type="checkbox"
            :disabled="isSubmitting"
          />
          <label class="form-check-label fw-semibold" for="bulkReviewCapture">
            Capture on amend
          </label>
        </div>
      </div>
    </div>

    <div class="bulk-review-list">
      <div class="bulk-review-list__heading">
        <div class="d-flex align-items-center gap-2">
          <input
            type="checkbox"
            class="form-check-input border border-primary"
            :checked="allSelected"
            @change="toggleSelectAll"
          />
          <span>Selected orders</span>
        </div>
        <span class="badge bg-primary">{{ selectedOrderUuids.length }} / {{ orders.length }}</span>
      </div>
      <div
        v-for="order in orders"
        :key="orderKey(order)"
        class="bulk-review-list__item"
        :class="{ 'bg-warning-subtle': isCompletedState(order.orderState) }"
      >
        <div class="d-flex align-items-center gap-2">
          <input
            type="checkbox"
            class="form-check-input border border-primary"
            :checked="selectedOrderUuids.includes(orderKey(order))"
            @change="toggleSelect(orderKey(order))"
          />
          <code>{{ orderKey(order) }}</code>
        </div>
        <div class="d-flex align-items-center gap-2">
          <span
            v-if="order.orderState"
            class="badge"
            :class="isCompletedState(order.orderState) ? 'bg-warning text-dark' : 'bg-secondary'"
          >
            {{ order.orderState }}
          </span>
          <span class="badge text-bg-dark">{{ order.classification || 'standard' }}</span>
        </div>
      </div>
    </div>

    <template #footer>
      <button class="btn btn-outline-secondary" :disabled="isSubmitting" @click="$emit('close')">
        Cancel
      </button>
      <button class="btn" :class="confirmButtonClass" :disabled="!canSubmit" @click="submit">
        <span v-if="isSubmitting">
          <span class="spinner-border spinner-border-sm me-1"></span>Creating job...
        </span>
        <span v-else><i class="fa fa-play me-1"></i>Confirm and Create Job</span>
      </button>
    </template>
  </PayOpsModalShell>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import PayOpsModalShell from '../shared/PayOpsModalShell.vue';
import { useAmendmentRoles } from '@/services/ryd-pay-operations/order-amendments/use-amendment-roles';
import {
  buildConfirmations,
  completedOrders as pickCompletedOrders,
  defaultSelection,
  escalateAction,
  isCompletedState,
  orderKey,
  requiresAdmin,
} from '@/services/ryd-pay-operations/order-amendments/bulk-selection';

const props = defineProps({
  visible: { type: Boolean, default: false },
  action: { type: String, default: '' },
  orders: { type: Array, default: () => [] },
  defaultReason: { type: String, default: '' },
  isSubmitting: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'submit']);
const { hasAdminAccess } = useAmendmentRoles();
const reason = ref('');
const captureOnAmend = ref(false);
const selectedOrderUuids = ref([]);

const completedOrders = computed(() => pickCompletedOrders(props.orders));

const effectiveAction = computed(() =>
  escalateAction(props.action, props.orders, selectedOrderUuids.value),
);

/**
 * Force-complete and refund-and-cancel are admin-only server-side. Block the
 * submit here so an agent gets an explanation instead of a bare 401 for the
 * whole batch.
 */
const blockedByRole = computed(
  () => requiresAdmin(effectiveAction.value) && !hasAdminAccess.value,
);

const destructive = computed(() =>
  ['cancelManually', 'refundAndCancelManually'].includes(effectiveAction.value),
);
const actionTone = computed(() => (destructive.value ? 'danger' : 'primary'));
const actionIcon = computed(() => (destructive.value ? 'fa-times-circle' : 'fa-tasks'));
const confirmButtonClass = computed(() => (destructive.value ? 'btn-danger' : 'btn-primary'));
const supportsCapture = computed(() =>
  ['completeManually', 'forceCompleteManually'].includes(effectiveAction.value),
);
const actionLabel = computed(() => {
  const labels = {
    cancelManually: 'Cancel',
    completeManually: 'Complete',
    forceCompleteManually: 'Force Complete',
    refundAndCancelManually: 'Refund & Cancel',
  };
  return labels[effectiveAction.value] || 'Bulk Action';
});

const allSelected = computed(
  () =>
    props.orders.length > 0 &&
    props.orders.every((o) => selectedOrderUuids.value.includes(orderKey(o))),
);

function toggleSelect(uuid) {
  const idx = selectedOrderUuids.value.indexOf(uuid);
  if (idx >= 0) selectedOrderUuids.value.splice(idx, 1);
  else selectedOrderUuids.value.push(uuid);
}

function toggleSelectAll() {
  if (allSelected.value) {
    selectedOrderUuids.value = [];
  } else {
    selectedOrderUuids.value = props.orders.map(orderKey).filter(Boolean);
  }
}

const canSubmit = computed(
  () =>
    reason.value.trim().length >= 10 &&
    selectedOrderUuids.value.length > 0 &&
    !blockedByRole.value &&
    !props.isSubmitting,
);

watch(
  () => [props.visible, props.orders],
  ([visible]) => {
    if (visible) {
      reason.value = props.defaultReason || '';
      captureOnAmend.value = false;
      // Already-final orders start unticked — re-amending them is an explicit act.
      selectedOrderUuids.value = defaultSelection(props.orders);
    }
  },
  { immediate: true },
);

function submit() {
  if (!canSubmit.value) return;
  const action = effectiveAction.value;
  emit('submit', {
    action,
    reason: reason.value.trim(),
    captureOnAmend: supportsCapture.value && captureOnAmend.value,
    orderUuids: [...selectedOrderUuids.value],
    confirmations: buildConfirmations(action),
  });
}
</script>

<style scoped>
.bulk-review-list {
  overflow: hidden;
  border: 1px solid #e1e5ee;
  border-radius: 0.75rem;
}

.bulk-review-list__heading,
.bulk-review-list__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0.9rem;
}

.bulk-review-list__heading {
  font-weight: 700;
  background: #f3f5fa;
}

.bulk-review-list__item + .bulk-review-list__item {
  border-top: 1px solid #edf0f5;
}

.bulk-review-list__item code {
  overflow-wrap: anywhere;
}
</style>
